import { baseApi } from "../api";
import {
  AdminActivityArgs,
  AdminActivityResponse,
  AdminCorporateAction,
  AdminInstrument,
  AdminInstrumentsArgs,
  AdminInstrumentsResponse,
  AdminInstrumentTodos,
  AdminJobRecoveryResult,
  AdminJobsArgs,
  AdminJobsResponse,
  AdminSectionResponse,
  AdminTodoResponse,
  BackfillQueueResponse,
  CoverageImpactResponse,
  SchedulerStatusResponse,
  SnapshotQueueResponse,
} from "./types";

const corporateActionReviewTags = [
  "AdminCorporateAction" as const,
  "AdminOverview" as const,
  "AdminAuditLog" as const,
];

const jobRecoveryTags = [
  "AdminJob" as const,
  "AdminOverview" as const,
  "AdminAuditLog" as const,
];

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminSnapshotQueue: builder.query<
      AdminSectionResponse<SnapshotQueueResponse>,
      void
    >({
      query: () => ({ url: "/admin/overview/snapshot-queue", method: "GET" }),
      providesTags: ["AdminOverview"],
    }),
    getAdminBackfillQueue: builder.query<
      AdminSectionResponse<BackfillQueueResponse>,
      void
    >({
      query: () => ({ url: "/admin/overview/backfill-queue", method: "GET" }),
      providesTags: ["AdminOverview"],
    }),
    getAdminCoverageImpact: builder.query<
      AdminSectionResponse<CoverageImpactResponse>,
      void
    >({
      query: () => ({ url: "/admin/overview/coverage-impact", method: "GET" }),
      providesTags: ["AdminOverview"],
    }),
    getAdminSchedulers: builder.query<
      AdminSectionResponse<SchedulerStatusResponse[]>,
      void
    >({
      query: () => ({ url: "/admin/overview/schedulers", method: "GET" }),
      providesTags: ["AdminOverview"],
    }),
    getAdminTodo: builder.query<AdminSectionResponse<AdminTodoResponse>, void>({
      query: () => ({ url: "/admin/overview/todo", method: "GET" }),
      providesTags: ["AdminOverview"],
    }),
    getAdminInstrumentTodos: builder.query<AdminInstrumentTodos, void>({
      query: () => ({ url: "/admin/instruments/todo", method: "GET" }),
      providesTags: ["AdminInstrumentTodo"],
    }),
    getAdminInstruments: builder.query<
      AdminSectionResponse<AdminInstrumentsResponse>,
      AdminInstrumentsArgs
    >({
      query: (params) => ({ url: "/admin/instruments", method: "GET", params }),
      providesTags: ["AdminInstrument"],
    }),
    getAdminInstrument: builder.query<AdminInstrument, number>({
      query: (id) => ({ url: `/admin/instruments/${id}`, method: "GET" }),
      providesTags: ["AdminInstrument"],
    }),
    getAdminJobs: builder.query<
      AdminSectionResponse<AdminJobsResponse>,
      AdminJobsArgs
    >({
      query: (params) => ({ url: "/admin/jobs", method: "GET", params }),
      providesTags: ["AdminJob"],
    }),
    getAdminActivity: builder.query<
      AdminSectionResponse<AdminActivityResponse>,
      AdminActivityArgs
    >({
      query: (params) => ({ url: "/admin/activity", method: "GET", params }),
      providesTags: ["AdminAuditLog"],
    }),
    retryAdminBackfill: builder.mutation<AdminJobRecoveryResult, number>({
      query: (id) => ({
        url: `/admin/jobs/backfill/${id}/retry`,
        method: "post",
      }),
      invalidatesTags: jobRecoveryTags,
    }),
    recalculateAdminSnapshot: builder.mutation<AdminJobRecoveryResult, number>({
      query: (id) => ({
        url: `/admin/jobs/snapshot/${id}/recalculate`,
        method: "post",
      }),
      invalidatesTags: jobRecoveryTags,
    }),
    getAdminCorporateActions: builder.query<AdminCorporateAction[], void>({
      query: () => ({ url: "/corporate-actions", method: "GET" }),
      providesTags: ["AdminCorporateAction"],
    }),
    deferCorporateAction: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/corporate-actions/${id}/defer`,
        method: "post",
      }),
      invalidatesTags: corporateActionReviewTags,
    }),
    resumeCorporateAction: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/corporate-actions/${id}/resume`,
        method: "post",
      }),
      invalidatesTags: corporateActionReviewTags,
    }),
    reconcileCorporateAction: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/corporate-actions/${id}/reconcile`,
        method: "post",
      }),
      invalidatesTags: corporateActionReviewTags,
    }),
  }),
});

export const {
  useGetAdminSnapshotQueueQuery,
  useGetAdminBackfillQueueQuery,
  useGetAdminCoverageImpactQuery,
  useGetAdminSchedulersQuery,
  useGetAdminTodoQuery,
  useGetAdminInstrumentTodosQuery,
  useGetAdminInstrumentsQuery,
  useGetAdminInstrumentQuery,
  useGetAdminJobsQuery,
  useGetAdminActivityQuery,
  useRetryAdminBackfillMutation,
  useRecalculateAdminSnapshotMutation,
  useGetAdminCorporateActionsQuery,
  useDeferCorporateActionMutation,
  useResumeCorporateActionMutation,
  useReconcileCorporateActionMutation,
} = adminApi;
